/**
 * Kapitel „Umsatz & Bezahlqualität" der Sektion „Anfos & Umsatz" (/auswertungen/umsatz).
 * Plan .claude/plans/2026-10-06-anfos-umsatz-sektion.md (P2), Umbau nach dem KPI-Review
 * .claude/plans/2026-10-23-p1-p2-kpi-review-umbau.md (Spezifikation P2-V3, Entscheidungen Till 23.10.2026).
 * Reihenfolge F4: K1 Jahr → K4b Nächste Rechnungen → K3 Kundentyp → K5 Halbjahre → K4a Erwartung gegen Ist →
 * K2 Rechnungsstufen → K6 Vertiefungen. Jede Karte lädt ihre Daten selbst (K4a/K4b teilen eine Anfrage).
 * Backend: /api/auswertungen/umsatz/*. Alle Euro-Werte netto (Till 08.10.2026). Karten und Tabellen starten offen (Till 23.10.).
 * Persona-Umbau C (28.10.2026, Plan .claude/plans/2026-10-28-persona-umbau-paket-1-2.md § C): drei Kopfkacheln mit Richtung
 * (C1), Reihenfolge K3 Kundentyp → K1 Jahr → K4b Nächste Rechnungen → K2 Rechnungsstufen → K4a Erwartung gegen Ist; die
 * Halbjahreskarte K5 entfällt (ihre Spalte „Läuft nach 6 Monaten noch" steht in K2), Wörterzeile am Seitenende.
 */
import { FC, Fragment } from 'react';
import { Box, Link, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import Woerterzeile from '../../../components/auswertungen/Woerterzeile';
import Explainer, { ExplainerSection } from '../../../components/Explainer';
import { LEITFRAGE_UMSATZ, TERMS } from '../../../helpers/kpiDefinitions';
import BezahlqualitaetKarte from './BezahlqualitaetKarte';
import KennzahlenKarte from './KennzahlenKarte';
import KopfKacheln from './KopfKacheln';
import KundentypKarte from './KundentypKarte';
import NaechsteRechnungenKarte from './NaechsteRechnungenKarte';
import PrognoseKarte from './PrognoseKarte';
// --- Workshop-Lücken 29.10.2026, Paket U ---
import GeldNachAlterKarte from './GeldNachAlterKarte';
import QualitaetQuelleKarte from './QualitaetQuelleKarte';
import VerlustBilanzKarte from './VerlustBilanzKarte';

const EXPLAINER: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: 'PL-X-Bestellungen mit ihren Zahlungsständen (zweimal täglich geliefert), die Preis-Konfiguration der Werbeaktionen, der Anfo-Wert jeder Anfo und die Cashflow-Prognose. Gezählt werden dieselben Anfos wie überall in dieser Sektion (ohne Stornos und Testbestellungen). Zeiträume enden am Datenstand des Bestellsystems, Vorjahre am selben Tag zur selben Uhrzeit.',
  },
  {
    label: 'Berechnung',
    body: 'Bezahlt heißt: über 1 € gezahlt — die 0,99-€-Testphasenrechnung zählt nicht. Bezahlquoten gelten nur für Anfos, die mindestens so alt sind wie die Reife; Zeilen, in denen mehr als ein Fünftel der Anfos jünger ist, stehen grau („jung“). Der Erwartete Wert ist der heutige Wert des Angebots jeder Anfo, ohne Angebotswert der Einzelwert ihres Produkts — auch für alte Anfos. Die Veränderung zum Vorjahr rechnet Anfos mal Ø Erwarteter Wert je Anfo. ▲/▼ erscheint nur, wo ein Test über beide Zeiträume den Unterschied vom Zufall trennt. Die nächsten Rechnungen kommen aus derselben Berechnung wie die Cashflow-Seite. Vorläufig ergänzte Anfos (Zahlstand noch unbekannt) zählen als Anfo, aber in keiner Quote.',
  },
  {
    label: 'Aussage',
    body: 'Wie viele Anfos zu zahlenden Kunden werden, wer nicht zahlt, wie viele Abos sich halten, und welche Rechnungen aus den laufenden Verträgen als Nächstes kommen.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Alle Euro-Beträge netto (ohne MwSt.); die alten Seiten Kündigung und Cashflow zeigen brutto. Was die Anfos bisher bezahlt haben, wächst mit ihrem Alter — es steht deshalb nur zusammen mit bezahlten Anfos und Ø Alter.',
  },
];

/** Vertiefungen zum Kapitel, je eine eigene Seite (P2-V3 § 2.8, Reihenfolge = Tabelle dort). */
export const UNTERSEITEN: { to: string; title: string; fuer: string; text: string }[] = [
  {
    to: '/auswertungen/umsatz/anmeldung-bestellung',
    title: 'Vom neuen Lead zur Bestellung',
    fuer: 'für Kampagnen',
    text: 'Wie viele neue Newsletter-Leads bestellen, wie schnell, womit — und was ein Lead wert ist.',
  },
  {
    to: '/auswertungen/umsatz/cashflow',
    title: 'Cashflow',
    fuer: 'für Finanzen, brutto',
    text: 'Kommt das Geld wie geplant? Zahlungen der nächsten 13 Wochen und Plan gegen Ist je Quartal (brutto, wie auf dem Konto).',
  },
  {
    to: '/auswertungen/umsatz/kuendigung?mode=date',
    title: 'Kündigungsquoten',
    fuer: 'für Controlling',
    text: 'Wie viele Quartalsabos die 1. und 2. Rechnung nicht bezahlen, je Bestellmonat — dazu Einzelfälle zum Nachprüfen.',
  },
  {
    to: '/auswertungen/umsatz/anfo-wert',
    title: 'Wie der Anfo-Wert entsteht',
    fuer: 'für Controlling: das Rechenmodell',
    text: 'Woraus sich der Erwartete Wert zusammensetzt — je Produkt, je Angebot und je Bestellung.',
  },  // --- P5 --- (27.10.2026): zwei weitere Alt-Seiten eingebettet, bisher nur unter „FIT Produkte".
  {
    to: '/auswertungen/umsatz/anfo-bewertung',
    title: 'Anfo-Bewertung',
    fuer: 'für Controlling, brutto',
    text: 'Anfo-Wert je Produkt gegen das, was die Anfos eines Zeitraums bisher bezahlt haben.',
  },
  {
    to: '/auswertungen/umsatz/diskrepanzen',
    title: 'Umsatz-Diskrepanzen',
    fuer: 'für Controlling, brutto',
    text: 'Bestellungen, bei denen das Gezahlte vom Betrag abweicht, den Preis und Laufzeit erwarten lassen.',
  },
];

const Unterseiten: FC = () => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: '8px', backgroundColor: 'transparent' }}>
    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
      Vertiefungen
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(260px, 100%), 1fr))', gap: 2 }}>
      {UNTERSEITEN.map((u) => (
        <Box key={u.to} sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', px: 2, py: 1.5 }}>
          <Link component={RouterLink} to={u.to} underline="hover" sx={{ fontWeight: 600 }}>
            {u.title}
          </Link>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: 12 }}>
            {u.fuer}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, lineHeight: 1.5 }}>
            {u.text}
          </Typography>
        </Box>
      ))}
    </Box>
    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
      Bezahlquote je Kanal:{' '}
      <Link component={RouterLink} to="/auswertungen/kanaele" underline="hover">
        Kanäle &amp; Werbeflächen →
      </Link>
    </Typography>
  </Paper>
);

/**
 * Kompakte Linkzeile oben (P5-Review #20): die Vertiefungen standen nur am Seitenende (y ≈ 7.600 von 7.940 px), oben war
 * nur Cashflow verlinkt. Dieselbe Liste wie unten (`UNTERSEITEN`), umbrochen nur zwischen den Einträgen.
 */
const UnterseitenZeile: FC = () => (
  <Typography variant="body2" component="div" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.8 }}>
    <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
      Unterseiten:
    </Box>
    {UNTERSEITEN.map((u, i) => (
      <Fragment key={u.to}>
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          <Link component={RouterLink} to={u.to} underline="always">
            {u.title}
          </Link>
        </Box>
        {i < UNTERSEITEN.length - 1 ? ' · ' : ''}
      </Fragment>
    ))}
  </Typography>
);

const Umsatz: FC = () => (
  <ChapterPage
    title="Umsatz & Bezahlqualität"
    question={LEITFRAGE_UMSATZ}
    intro={
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
        {TERMS.anfos.glossar} · alle Euro netto (ohne MwSt.); die alten Seiten
        Kündigung und Cashflow zeigen brutto · grau = {TERMS.jung.short} oder wenig Anfos
      </Typography>
    }
    standQuellen={[
      { key: 'plx_orders', label: 'Bestellsystem (PL-X)' },
      { key: 'anfo_werte', label: 'Anfo-Werte' },
      { key: 'px_ergaenzung', label: 'Nachträge aus dem PX-Export (vorläufig)' },
    ]}
  >
    <UnterseitenZeile />
    <KopfKacheln />
    <Explainer title="Wie dieses Kapitel rechnet" sections={EXPLAINER} />
    <KundentypKarte />
    {/* Audit 30.10.2026 E4: „Dieses Jahr gegen die Vorjahre“ direkt unter „Wer bestellt — und wer zahlt“, vor der
        Qualität je Quelle (N3). Workshop-Lücken 29.10.2026 (Paket U): N1 vor K2, A4 nach K4a. */}
    <KennzahlenKarte />
    <QualitaetQuelleKarte />
    <NaechsteRechnungenKarte />
    <VerlustBilanzKarte />
    <BezahlqualitaetKarte />
    <PrognoseKarte />
    <GeldNachAlterKarte />
    <Unterseiten />
    <Woerterzeile kapitel="umsatz" />
  </ChapterPage>
);

export default Umsatz;
